import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import CalendarSidebarSection from './CalendarSidebarSection';
import { dataRepository } from '../repository/DataRepository';
import AuthStatus from './AuthStatus';
import FileUploader from './FileUploader';
import NotificationSettings from './NotificationSettings';
import BookLookup from './BookLookup';
import SourceFormModal from './SourceFormModal';
import type { BookInfo } from '../services/googleBooksService';
import type { ObjectType, UserCollection } from '../models/types';
import myelinLogo from '../assets/myelin-logo-circle.png';
import './Sidebar.css';
import {
  Home,
  CheckSquare,
  Target,
  MapPin,
  Home as Building,
  FileText,
  BookOpen,
  Library,
  Archive,
  Tag,
  FolderOpen,
  List as ListIcon,
  Calendar as CalendarIcon,
  TrendingUp,
  Sparkles,
  Network,
  Inbox,
  Settings,
  Database,
  Bell,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  User,
  GripVertical,
  Paperclip,
  StickyNote,
  MessageSquare,
  ClipboardPaste
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DragEndEvent } from '@dnd-kit/core';

// Navigation item types
interface NavItem {
  id: string;
  path: string;
  icon: React.ReactNode;
  label: string;
  action?: () => void; // For special actions like notifications
}

// Sortable Nav Item Component
interface SortableNavItemProps {
  item: NavItem;
  sidebarOpen: boolean;
  onNavigate: (path: string) => void;
  onAction?: () => void;
}

function SortableNavItem({ item, sidebarOpen, onNavigate, onAction }: SortableNavItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    if (item.action) {
      item.action();
      if (onAction) onAction();
    } else {
      onNavigate(item.path);
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="sortable-nav-item">
      <button
        className="nav-drag-handle"
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        type="button"
      >
        <GripVertical size={14} />
      </button>
      <button onClick={handleClick} className="nav-link" title={item.label} type="button">
        {item.icon}
        {sidebarOpen && <span>{item.label}</span>}
      </button>
    </div>
  );
}

// Default navigation items
const DEFAULT_VIEWS: NavItem[] = [
  { id: 'tasks', path: '/tasks', icon: <CheckSquare size={18} />, label: 'All Tasks' },
  { id: 'projects', path: '/projects', icon: <Target size={18} />, label: 'Projects' },
  { id: 'maps', path: '/maps', icon: <MapPin size={18} />, label: 'Maps' },
  { id: 'areas', path: '/areas', icon: <Building size={18} />, label: 'Areas' },
  { id: 'notes', path: '/notes', icon: <FileText size={18} />, label: 'Notes' },
  { id: 'sources', path: '/sources', icon: <Library size={18} />, label: 'Sources' },
  { id: 'reading-list', path: '/reading-list', icon: <BookOpen size={18} />, label: 'Reading List' },
  { id: 'archived', path: '/archived', icon: <Archive size={18} />, label: 'Archived' },
  { id: 'tags', path: '/tags', icon: <Tag size={18} />, label: 'Tags' },
  { id: 'collections', path: '/lists', icon: <ListIcon size={18} />, label: 'Lists' },
];

export default function Sidebar() {
  const { addTopLevelObject, toggleSidebar, sidebarOpen } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isCalendarRoute = location.pathname === '/calendar';
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [typeSelectorPosition, setTypeSelectorPosition] = useState<{ x: number; y: number; width: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(180);
  const [viewsExpanded, setViewsExpanded] = useState(true);
  const [toolsExpanded, setToolsExpanded] = useState(true);
  const [quickAccessExpanded, setQuickAccessExpanded] = useState(true);
  const [pinnedCollections, setPinnedCollections] = useState<UserCollection[]>([]);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showBookLookup, setShowBookLookup] = useState(false);
  const [showSourceTypeSelector, setShowSourceTypeSelector] = useState(false);
  const [showSourceForm, setShowSourceForm] = useState(false);
  const [selectedSourceType, setSelectedSourceType] = useState<{ id: string; icon: string; label: string } | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const newObjectBtnRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Navigation order state
  const [viewsOrder, setViewsOrder] = useState<NavItem[]>(() => {
    const saved = localStorage.getItem('sidebar-views-order');
    if (saved) {
      try {
        const savedIds = JSON.parse(saved) as string[];
        // Reconstruct items in saved order, adding any new items at the end
        const orderedItems = savedIds
          .map(id => DEFAULT_VIEWS.find(v => v.id === id))
          .filter((item): item is NavItem => item !== undefined);
        // Add any new items that weren't in saved order
        const newItems = DEFAULT_VIEWS.filter(v => !savedIds.includes(v.id));
        return [...orderedItems, ...newItems];
      } catch {
        return DEFAULT_VIEWS;
      }
    }
    return DEFAULT_VIEWS;
  });

  const [toolsOrder, setToolsOrder] = useState<NavItem[]>(() => {
    const DailyCalendarIcon = ({ size }: { size: number }) => (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="2" width="2" height="4" rx="1" fill="currentColor" />
        <rect x="16" y="2" width="2" height="4" rx="1" fill="currentColor" />
        <rect x="3" y="5" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
        <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2" />
        <text x="12" y="17" textAnchor="middle" fill="currentColor" fontSize="10" fontWeight="700">
          {new Date().getDate()}
        </text>
      </svg>
    );

    const DEFAULT_TOOLS: NavItem[] = [
      { id: 'daily', path: '/daily', icon: <DailyCalendarIcon size={18} />, label: 'Daily Notes' },
      { id: 'calendar', path: '/calendar', icon: <CalendarIcon size={18} />, label: 'Calendar' },
      { id: 'habits', path: '/habits', icon: <TrendingUp size={18} />, label: 'Habits' },
      { id: 'ai-search', path: '/ai-search', icon: <Sparkles size={18} />, label: 'AI Search' },
      { id: 'chat', path: '/chat', icon: <MessageSquare size={18} />, label: 'AI Chat' },
      { id: 'mdm', path: '/mdm', icon: <ClipboardPaste size={18} />, label: 'ED MDM' },
      { id: 'graph', path: '/graph', icon: <Network size={18} />, label: 'Graph View' },
      { id: 'processing', path: '/processing', icon: <Inbox size={18} />, label: 'Processing Center' },
      { id: 'all-objects', path: '/all-objects', icon: <Database size={18} />, label: 'All Objects' },
      { id: 'notifications', path: '', icon: <Bell size={18} />, label: 'Notifications', action: () => setShowNotificationSettings(true) },
    ];

    const saved = localStorage.getItem('sidebar-tools-order');
    if (saved) {
      try {
        const savedIds = JSON.parse(saved) as string[];
        const orderedItems = savedIds
          .map(id => DEFAULT_TOOLS.find(t => t.id === id))
          .filter((item): item is NavItem => item !== undefined);
        const newItems = DEFAULT_TOOLS.filter(t => !savedIds.includes(t.id));
        return [...orderedItems, ...newItems];
      } catch {
        return DEFAULT_TOOLS;
      }
    }
    return DEFAULT_TOOLS;
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= 150 && newWidth <= 400) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  // Drag end handlers
  const handleViewsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = viewsOrder.findIndex(v => v.id === active.id);
    const newIndex = viewsOrder.findIndex(v => v.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(viewsOrder, oldIndex, newIndex);
    setViewsOrder(newOrder);
    localStorage.setItem('sidebar-views-order', JSON.stringify(newOrder.map(v => v.id)));
  };

  const handleToolsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = toolsOrder.findIndex(t => t.id === active.id);
    const newIndex = toolsOrder.findIndex(t => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(toolsOrder, oldIndex, newIndex);
    setToolsOrder(newOrder);
    localStorage.setItem('sidebar-tools-order', JSON.stringify(newOrder.map(t => t.id)));
  };

  // Load pinned collections for Quick Access
  useEffect(() => {
    const loadPinnedCollections = async () => {
      try {
        const pinned = await dataRepository.getPinnedUserCollections();
        setPinnedCollections(pinned);
      } catch (error) {
        console.error('Error loading pinned collections:', error);
      }
    };

    loadPinnedCollections();
  }, []);

  const handleCreateObject = async (type: ObjectType) => {
    try {
      // Special case for file type - show file uploader
      if (type === 'file') {
        setShowTypeSelector(false);
        setShowFileUploader(true);
        return;
      }

      // Special case for source type - show source type selector
      if (type === 'source') {
        console.log('[Sidebar] Source selected - showing source type selector');
        setShowTypeSelector(false);
        setShowSourceTypeSelector(true);
        return;
      }

      const typeLabels: Record<ObjectType, string> = {
        map: 'Map',
        project: 'Project',
        area: 'Life Area',
        resource: 'Resource',
        archive: 'Archive',
        file: 'File',
        book: 'Book',
        person: 'Person',
        meeting: 'Meeting',
        task: 'Task',
        note: 'Note',
        query: 'Query',
        source: 'Source'
      };

      // Generate unique name with timestamp
      const baseLabel = typeLabels[type];
      const objectName = `New ${baseLabel}`;

      const newObject = await dataRepository.createObject(objectName, type);
      addTopLevelObject(newObject);
      navigate(`/object/${newObject.id}`);
      setShowTypeSelector(false);
    } catch (error) {
      console.error('Error creating object:', error);
    }
  };

  const handleFileUploaded = (objectId: string) => {
    setShowFileUploader(false);
    navigate(`/object/${objectId}`);
  };

  const handleBookSelect = async (book: BookInfo) => {
    try {
      console.log('[Sidebar] Creating source from book:', book.title);

      // Create the source object with the book title
      const newObject = await dataRepository.createObject(
        book.title,
        'source',
        book.description || ''
      );

      // Add book properties (don't await each one to avoid hanging)
      const propertiesToAdd = [
        { name: 'author', value: book.authors?.join(', ') || '', type: 'text' as const },
        { name: 'publisher', value: book.publisher || '', type: 'text' as const },
        { name: 'published', value: book.publishedDate || '', type: 'text' as const },
        { name: 'isbn', value: book.isbn13 || book.isbn10 || '', type: 'text' as const },
        { name: 'pages', value: book.pageCount ? String(book.pageCount) : '', type: 'number' as const },
        { name: 'sourceType', value: 'book', type: 'text' as const },
        { name: 'googleBooksId', value: book.id, type: 'text' as const },
        { name: 'coverUrl', value: book.thumbnail || '', type: 'url' as const },
      ].filter(p => p.value);

      // Create properties in parallel for speed
      await Promise.all(
        propertiesToAdd.map(prop =>
          dataRepository.createProperty(newObject.id, prop.name, prop.type, prop.value)
            .catch(err => console.warn(`[Sidebar] Failed to create property ${prop.name}:`, err))
        )
      );

      // Add tags if categories exist
      if (book.categories && book.categories.length > 0) {
        await dataRepository.updateObject(newObject.id, {
          tags: book.categories.slice(0, 3) // Max 3 categories as tags
        }).catch(err => console.warn('[Sidebar] Failed to add tags:', err));
      }

      // Create visual book info block on the canvas
      let blockY = 50;

      // Create cover image block if thumbnail exists
      if (book.thumbnail) {
        await dataRepository.createBlock({
          objectId: newObject.id,
          type: 'image',
          content: book.thumbnail,
          x: 50,
          y: blockY,
          width: 150,
          height: 220,
          zIndex: 1,
          metadata: {
            alt: `Cover of ${book.title}`,
            source: 'google-books'
          }
        }).catch(err => console.warn('[Sidebar] Failed to create cover image block:', err));
      }

      // Create book info text block
      const authorText = book.authors?.join(', ') || 'Unknown Author';
      const yearText = book.publishedDate ? book.publishedDate.substring(0, 4) : '';
      const pagesText = book.pageCount ? `${book.pageCount} pages` : '';

      const infoHtml = `<p><strong>${book.title}</strong></p>` +
        (book.subtitle ? `<p><em>${book.subtitle}</em></p>` : '') +
        `<p>by ${authorText}</p>` +
        (yearText ? `<p>${yearText}${pagesText ? ` · ${pagesText}` : ''}</p>` : '') +
        (book.publisher ? `<p style="color: #888; font-size: 0.9em">${book.publisher}</p>` : '');

      await dataRepository.createBlock({
        objectId: newObject.id,
        type: 'text',
        content: infoHtml,
        x: book.thumbnail ? 220 : 50,  // Position next to cover if it exists
        y: blockY,
        width: 300,
        height: 150,
        zIndex: 2,
        metadata: {
          bookInfo: true,
          googleBooksId: book.id
        }
      }).catch(err => console.warn('[Sidebar] Failed to create book info block:', err));

      // Create description block if description exists
      if (book.description) {
        const descriptionY = blockY + (book.thumbnail ? 240 : 170); // Below the cover/info
        await dataRepository.createBlock({
          objectId: newObject.id,
          type: 'text',
          content: `<p><strong>Description</strong></p><p>${book.description}</p>`,
          x: 50,
          y: descriptionY,
          width: book.thumbnail ? 470 : 300,  // Full width if cover exists
          height: 200,
          zIndex: 3,
          metadata: {
            bookDescription: true,
            googleBooksId: book.id
          }
        }).catch(err => console.warn('[Sidebar] Failed to create description block:', err));
      }

      console.log('[Sidebar] Source created successfully:', newObject.id);
      addTopLevelObject(newObject);
      setShowBookLookup(false);
      navigate(`/object/${newObject.id}`);
    } catch (error) {
      console.error('[Sidebar] Error creating source from book:', error);
      // Still close the modal and show error
      setShowBookLookup(false);
    }
  };

  const handleSkipBookLookup = async () => {
    // Allow creating a source without book lookup
    try {
      const newObject = await dataRepository.createObject('New Source', 'source');
      addTopLevelObject(newObject);
      setShowBookLookup(false);
      navigate(`/object/${newObject.id}`);
    } catch (error) {
      console.error('[Sidebar] Error creating empty source:', error);
      setShowBookLookup(false);
    }
  };

  // Source type options
  const sourceTypes = [
    { id: 'book', icon: '📚', label: 'Book', description: 'Search Google Books for metadata' },
    { id: 'film', icon: '🎬', label: 'Film / TV', description: 'Movie or TV show' },
    { id: 'podcast', icon: '🎙️', label: 'Podcast', description: 'Podcast episode or series' },
    { id: 'article', icon: '📰', label: 'Article', description: 'Web article or blog post' },
    { id: 'video', icon: '🎥', label: 'Video', description: 'YouTube or other video' },
    { id: 'quote', icon: '💬', label: 'Quote', description: 'Quote or excerpt' },
    { id: 'file', icon: '📄', label: 'File', description: 'Upload a file' },
    { id: 'other', icon: '📎', label: 'Other', description: 'Other source type' },
  ];

  const handleSourceTypeSelect = async (sourceTypeId: string) => {
    setShowSourceTypeSelector(false);

    // Book - show Book Lookup
    if (sourceTypeId === 'book') {
      setShowBookLookup(true);
      return;
    }

    // File - show File Uploader
    if (sourceTypeId === 'file') {
      setShowFileUploader(true);
      return;
    }

    // All other types - show source form modal with type-specific fields
    const sourceType = sourceTypes.find(t => t.id === sourceTypeId);
    if (sourceType) {
      setSelectedSourceType({ id: sourceType.id, icon: sourceType.icon, label: sourceType.label });
      setShowSourceForm(true);
    }
  };

  const handleSourceFormCreated = (objectId: string) => {
    setShowSourceForm(false);
    setSelectedSourceType(null);
    navigate(`/object/${objectId}`);
  };

  // Search functionality
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const allObjects = await dataRepository.getAllObjects();
        const query = searchQuery.toLowerCase();

        const results = allObjects
          .filter(obj => {
            const name = String(obj.name || '').toLowerCase();
            const content = String(obj.content || '').toLowerCase();
            const type = obj.type.toLowerCase();
            const tags = (obj.tags || []).join(' ').toLowerCase();

            return name.includes(query) ||
                   content.includes(query) ||
                   type.includes(query) ||
                   tags.includes(query);
          })
          .slice(0, 20); // Limit to 20 results

        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(performSearch, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSearchResultClick = (objectId: string) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/object/${objectId}`);
  };

  const getObjectIcon = (type: ObjectType) => {
    const iconProps = { size: 16 };
    const icons: Record<ObjectType, JSX.Element> = {
      map: <MapPin {...iconProps} />,
      project: <Target {...iconProps} />,
      area: <Building {...iconProps} />,
      resource: <BookOpen {...iconProps} />,
      archive: <Archive {...iconProps} />,
      file: <Paperclip {...iconProps} />,
      book: <BookOpen {...iconProps} />,
      person: <User {...iconProps} />,
      meeting: <CalendarIcon {...iconProps} />,
      task: <CheckSquare {...iconProps} />,
      note: <StickyNote {...iconProps} />,
      query: <Search {...iconProps} />,
      source: <BookOpen {...iconProps} />
    };
    return icons[type] || <FileText {...iconProps} />;
  };

  // Get current day of month for Daily Notes button
  const currentDay = new Date().getDate();

  const collapsedWidth = 60;
  const effectiveWidth = sidebarOpen ? sidebarWidth : collapsedWidth;

  // Custom calendar icon component with current day
  const DailyCalendarIcon = ({ size = 20 }: { size?: number }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="daily-calendar-icon"
    >
      {/* Calendar binding rings */}
      <rect x="6" y="2" width="2" height="4" rx="1" fill="currentColor" />
      <rect x="16" y="2" width="2" height="4" rx="1" fill="currentColor" />

      {/* Calendar body */}
      <rect x="3" y="5" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />

      {/* Top bar */}
      <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="2" />

      {/* Day number */}
      <text
        x="12"
        y="17"
        textAnchor="middle"
        fill="currentColor"
        fontSize="10"
        fontWeight="700"
      >
        {currentDay}
      </text>
    </svg>
  );

  return (
    <aside ref={sidebarRef} className={`sidebar ${!sidebarOpen ? 'collapsed' : ''}`} style={{ width: `${effectiveWidth}px` }}>
      {sidebarOpen && (
        <div
          className={`sidebar-resize-handle ${isResizing ? 'resizing' : ''}`}
          onMouseDown={handleResizeStart}
        />
      )}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <img src={myelinLogo} alt="Myelin" className="sidebar-logo" />
          {sidebarOpen && <h1>Myelin</h1>}
        </div>
        <div className="sidebar-header-actions">
          {sidebarOpen && (
            <button onClick={() => setShowSearch(true)} className="search-btn" title="Search (Ctrl+K)" type="button">
              <Search size={16} />
            </button>
          )}
          <button onClick={toggleSidebar} className="close-btn" type="button" title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}>
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
      </div>

      <div className="sidebar-nav">
        <div className="nav-section">
          <button onClick={() => navigate('/')} className="nav-link" title="Home">
            <Home size={18} />
            {sidebarOpen && <span>Home</span>}
          </button>
        </div>

        {/* Route-aware sections — render near the top for visibility */}
        {isCalendarRoute && sidebarOpen && <CalendarSidebarSection />}

        <div className="nav-section">
          {sidebarOpen && (
            <div className="nav-section-title" onClick={() => setViewsExpanded(!viewsExpanded)}>
              <span className="collapse-icon">{viewsExpanded ? '▼' : '▶'}</span>
              Views
            </div>
          )}
          {viewsExpanded && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleViewsDragEnd}
            >
              <SortableContext
                items={viewsOrder.map(v => v.id)}
                strategy={verticalListSortingStrategy}
              >
                {viewsOrder.map(item => (
                  <SortableNavItem
                    key={item.id}
                    item={item}
                    sidebarOpen={sidebarOpen}
                    onNavigate={navigate}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
        {/* Quick Access - Pinned Collections */}
        {pinnedCollections.length > 0 && (
          <div className="nav-section">
            {sidebarOpen && (
              <div className="nav-section-title" onClick={() => setQuickAccessExpanded(!quickAccessExpanded)}>
                <span className="collapse-icon">{quickAccessExpanded ? '▼' : '▶'}</span>
                Quick Access
              </div>
            )}
            {quickAccessExpanded && (
              <>
                {pinnedCollections.map(collection => (
                  <button
                    key={collection.id}
                    onClick={() => navigate(`/lists?id=${collection.id}`)}
                    className="nav-link collection-link"
                    type="button"
                    title={collection.name}
                  >
                    {collection.icon} {sidebarOpen && collection.name}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
        <div className="nav-section">
          {sidebarOpen && (
            <div className="nav-section-title" onClick={() => setToolsExpanded(!toolsExpanded)}>
              <span className="collapse-icon">{toolsExpanded ? '▼' : '▶'}</span>
              Tools
            </div>
          )}
          {toolsExpanded && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleToolsDragEnd}
            >
              <SortableContext
                items={toolsOrder.map(t => t.id)}
                strategy={verticalListSortingStrategy}
              >
                {toolsOrder.map(item => (
                  <SortableNavItem
                    key={item.id}
                    item={item}
                    sidebarOpen={sidebarOpen}
                    onNavigate={navigate}
                    onAction={item.action}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <div className="sidebar-actions">
        <button
          ref={newObjectBtnRef}
          onClick={() => {
            if (!showTypeSelector && newObjectBtnRef.current) {
              const rect = newObjectBtnRef.current.getBoundingClientRect();
              console.log('[Sidebar] Button position:', rect);

              // Calculate available space below and above
              const spaceBelow = window.innerHeight - rect.bottom - 20;
              const spaceAbove = rect.top - 20;

              // Estimate menu height (approximately 400px for all items)
              const estimatedMenuHeight = 400;

              let yPosition;
              if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
                // Not enough space below, position above the button
                yPosition = rect.top - estimatedMenuHeight - 4;
                console.log('[Sidebar] Positioning above button, spaceBelow:', spaceBelow, 'spaceAbove:', spaceAbove);
              } else {
                // Position below the button
                yPosition = rect.bottom + 4;
                console.log('[Sidebar] Positioning below button, spaceBelow:', spaceBelow, 'spaceAbove:', spaceAbove);
              }

              const position = {
                x: rect.left,
                y: Math.max(10, yPosition), // Ensure minimum 10px from top
                width: rect.width
              };
              console.log('[Sidebar] Setting position:', position);
              setTypeSelectorPosition(position);
              setShowTypeSelector(true);
            } else {
              setShowTypeSelector(false);
              setTypeSelectorPosition(null);
            }
          }}
          className="btn-primary"
          title="New Object"
        >
          <Plus size={sidebarOpen ? 12 : 20} />
          {sidebarOpen && <span>New Object</span>}
        </button>
        {showTypeSelector && typeSelectorPosition && createPortal(
          <>
            <div
              className="type-selector-overlay"
              onClick={() => setShowTypeSelector(false)}
            />
            <div
              className="type-selector"
              style={{
                position: 'fixed',
                left: `${typeSelectorPosition.x}px`,
                top: `${typeSelectorPosition.y}px`,
                minWidth: `${typeSelectorPosition.width}px`
              }}
            >
              <div className="type-category">Containers</div>
              <button onClick={() => handleCreateObject('map')}><span><MapPin size={16} /></span> Map</button>
              <button onClick={() => handleCreateObject('project')}><span><Target size={16} /></span> Project</button>
              <button onClick={() => handleCreateObject('area')}><span><Building size={16} /></span> Area</button>
              <div className="type-category">Notes</div>
              <button onClick={() => handleCreateObject('note')}><span><StickyNote size={16} /></span> Note</button>
              <button onClick={() => handleCreateObject('meeting')}><span><CalendarIcon size={16} /></span> Meeting</button>
              <button onClick={() => handleCreateObject('person')}><span><User size={16} /></span> Person</button>
              <button onClick={() => handleCreateObject('task')}><span><CheckSquare size={16} /></span> Task</button>
              <button onClick={() => handleCreateObject('query')}><span><Search size={16} /></span> Query</button>
              <div className="type-category">Sources</div>
              <button onClick={() => handleCreateObject('source')}><span><BookOpen size={16} /></span> Source</button>
            </div>
          </>,
          document.body
        )}
      </div>

      {/* User section */}
      <div className="sidebar-user">
        <button onClick={() => navigate('/admin')} className="user-admin-btn" title="Admin">
          <User size={18} />
          {sidebarOpen && <span>Admin</span>}
        </button>
      </div>

      <AuthStatus />

      {showFileUploader && (
        <FileUploader
          onFileUploaded={handleFileUploaded}
          onClose={() => setShowFileUploader(false)}
        />
      )}

      {showBookLookup && (
        <BookLookup
          onSelect={handleBookSelect}
          onClose={() => setShowBookLookup(false)}
          onSkip={handleSkipBookLookup}
        />
      )}

      {showSourceForm && selectedSourceType && createPortal(
        <SourceFormModal
          sourceTypeId={selectedSourceType.id}
          sourceTypeLabel={selectedSourceType.label}
          sourceTypeIcon={selectedSourceType.icon}
          onClose={() => {
            setShowSourceForm(false);
            setSelectedSourceType(null);
          }}
          onCreated={handleSourceFormCreated}
        />,
        document.body
      )}

      {showSourceTypeSelector && createPortal(
        <div className="source-type-selector-overlay" onClick={() => setShowSourceTypeSelector(false)}>
          <div className="source-type-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="source-type-selector-header">
              <h2>What type of source?</h2>
              <button className="close-btn" onClick={() => setShowSourceTypeSelector(false)}>×</button>
            </div>
            <div className="source-type-selector-grid">
              {sourceTypes.map(st => (
                <button
                  key={st.id}
                  className="source-type-option"
                  onClick={() => handleSourceTypeSelect(st.id)}
                >
                  <span className="source-type-icon">{st.icon}</span>
                  <span className="source-type-label">{st.label}</span>
                  <span className="source-type-desc">{st.description}</span>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showNotificationSettings && createPortal(
        <div className="notification-settings-modal" onClick={() => setShowNotificationSettings(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <NotificationSettings onClose={() => setShowNotificationSettings(false)} />
          </div>
        </div>,
        document.body
      )}

      {showSearch && createPortal(
        <>
          <div className="search-overlay" onClick={() => setShowSearch(false)} />
          <div className="search-modal">
            <div className="search-modal-header">
              <input
                ref={searchInputRef}
                type="text"
                className="search-modal-input"
                placeholder="Search objects by name, content, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowSearch(false);
                    setSearchQuery('');
                  }
                }}
              />
              <button className="search-modal-close" onClick={() => setShowSearch(false)} type="button">
                ×
              </button>
            </div>

            <div className="search-modal-results">
              {isSearching && (
                <div className="search-loading">Searching...</div>
              )}

              {!isSearching && searchQuery && searchResults.length === 0 && (
                <div className="search-empty">No results found for "{searchQuery}"</div>
              )}

              {!isSearching && searchResults.length > 0 && (
                <>
                  <div className="search-results-header">
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  </div>
                  {searchResults.map(obj => (
                    <div
                      key={obj.id}
                      className="search-result-item"
                      onClick={() => handleSearchResultClick(obj.id)}
                    >
                      <div className="search-result-icon">{getObjectIcon(obj.type)}</div>
                      <div className="search-result-content">
                        <div className="search-result-name">
                          {typeof obj.name === 'string' ? obj.name : 'Untitled'}
                        </div>
                        <div className="search-result-meta">
                          <span className="search-result-type">{obj.type}</span>
                          {obj.updatedAt && (
                            <span className="search-result-date">
                              • {new Date(obj.updatedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {!searchQuery && (
                <div className="search-tips">
                  <div className="search-tip-title">Search tips:</div>
                  <ul>
                    <li>Type to search by name, content, or type</li>
                    <li>Results update as you type</li>
                    <li>Press <kbd>Esc</kbd> to close</li>
                    <li>Use <kbd>Ctrl+K</kbd> to open search (coming soon)</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </aside>
  );
}
